import {
    PublicClientApplication,
    Configuration,
    AccountInfo,
    SilentRequest,
    RedirectRequest,
    InteractionRequiredAuthError
} from '@azure/msal-browser';
import { Role, ActiveLicenses, OrganisationClaim, User } from '../types';
import { apiClient } from '@core/api';

// ============ Environment variables ============

const getEnvVar = (key: string, fallback?: string): string => {
    const value = import.meta.env[key];
    if (!value) {
        if (fallback) {
            console.warn(`[AuthService] Environment variable ${key} is not set, using fallback: ${fallback}`);
            return fallback;
        }
        throw new Error(`Required environment variable ${key} is not set`);
    }
    return value;
};

const AUTH_CLIENTID = getEnvVar('VITE_AUTH_CLIENTID');
const AUTH_ENTRA_TENANT_NAME = getEnvVar('VITE_AUTH_ENTRA_TENANT_NAME');
const AUTH_ENTRA_TENANT_ID = getEnvVar('VITE_AUTH_ENTRA_TENANT_ID');
const VITE_BACKEND_CLIENT_ID = getEnvVar('VITE_BACKEND_CLIENT_ID');
const APP_URL = getEnvVar('VITE_URL', window.location.origin);

// ============ MSAL configuration ============

const msalConfig: Configuration = {
    auth: {
        clientId: AUTH_CLIENTID,
        authority: `https://${AUTH_ENTRA_TENANT_NAME}.ciamlogin.com/${AUTH_ENTRA_TENANT_ID}`,
        redirectUri: APP_URL,
        knownAuthorities: [`${AUTH_ENTRA_TENANT_NAME}.ciamlogin.com`]
    },
    cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false
    }
};

export const API_SCOPES = [`api://${VITE_BACKEND_CLIENT_ID}/rw`];

const loginRequest: RedirectRequest = {
    scopes: API_SCOPES
};

const silentRequest: SilentRequest = {
    scopes: API_SCOPES,
    account: null as any
};

// ============ Shared MSAL instance ============
export const msalInstance = new PublicClientApplication(msalConfig);

// ============ AuthService ============
export class AuthService {
    private userCallback?: (user: User | null) => void;
    private clearAuthCallback?: () => void;
    private isInitialized = false;
    private interactionInProgress = false;
    private tokenRenewalPromise: Promise<string | null> | null = null;

    setCallbacks(userCallback: (user: User | null) => void, clearAuthCallback?: () => void): void {
        this.userCallback = userCallback;
        this.clearAuthCallback = clearAuthCallback;
    }

    async handlePageLoad(): Promise<void> {
        try {
            // Process any pending redirect response (returning from loginRedirect)
            const response = await msalInstance.handleRedirectPromise();

            this.interactionInProgress = false;
            this.isInitialized = true;

            let account: AccountInfo | null;

            if (response?.account) {
                // Returning from loginRedirect — account is in the response
                account = response.account;
                msalInstance.setActiveAccount(account);
            } else {
                // Normal page load — active account restored from sessionStorage in main.tsx
                account = msalInstance.getActiveAccount() ?? null;
            }

            if (account) {
                await this.setUserFromAccount(account);
            } else {
                await this.loginRedirect();
            }
        } catch (error) {
            this.isInitialized = false;
            this.interactionInProgress = false;
            console.error('[AuthService] handlePageLoad failed:', error);
            throw error;
        }
    }

    async loginRedirect(): Promise<void> {
        this.interactionInProgress = true;
        try {
            await msalInstance.loginRedirect(loginRequest);
        } catch (error: any) {
            if (error?.errorCode === 'interaction_in_progress') {
                console.log('[AuthService] Login redirect already in progress');
                return;
            }
            this.interactionInProgress = false;
            console.error('[AuthService] Login redirect failed:', error);
            throw error;
        }
    }

    async logout(): Promise<void> {
        this.interactionInProgress = true;
        try {
            const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
            await msalInstance.logoutRedirect({ account });
        } catch (error) {
            this.interactionInProgress = false;
            console.error('[AuthService] Logout failed:', error);
            this.clearAuthCallback?.();
        }
    }

    async getAccessTokenSilent(): Promise<string | null> {
        const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
        if (!account) {
            return null;
        }

        if (!this.isInitialized || this.interactionInProgress) {
            console.warn('[AuthService] Skipping token acquisition — not ready or interaction in progress');
            return null;
        }

        if (this.tokenRenewalPromise) {
            return this.tokenRenewalPromise;
        }

        try {
            const response = await msalInstance.acquireTokenSilent({
                ...silentRequest,
                account
            });
            return response.accessToken;
        } catch (error) {
            const errorCode = (error as any)?.errorCode;

            const isAuthFailure =
                error instanceof InteractionRequiredAuthError ||
                errorCode === 'monitor_window_timeout' ||
                errorCode === 'login_required' ||
                errorCode === 'consent_required' ||
                errorCode === 'invalid_grant';

            if (isAuthFailure) {
                if (!this.tokenRenewalPromise) {
                    this.tokenRenewalPromise = this.redirectForToken(account).finally(() => {
                        this.tokenRenewalPromise = null;
                    });
                }
                return this.tokenRenewalPromise;
            } else {
                console.error('[AuthService] Silent token acquisition failed:', error);
            }

            return null;
        }
    }

    private async redirectForToken(account: AccountInfo): Promise<string | null> {
        this.interactionInProgress = true;
        try {
            await msalInstance.acquireTokenRedirect({ ...silentRequest, account });
        } catch (error: any) {
            if (error?.errorCode === 'interaction_in_progress') {
                console.log('[AuthService] Redirect already in progress');
                return null;
            }
            this.interactionInProgress = false;
            console.error('[AuthService] Redirect for token failed, for logout', error);
            this.clearAuthCallback?.();
        }
        return null;
    }

    isAuthenticated(): boolean {
        return msalInstance.getAllAccounts().length > 0;
    }

    getCurrentAccount(): AccountInfo | null {
        return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
    }

    private async setUserFromAccount(account: AccountInfo): Promise<void> {
        const claims = account.idTokenClaims as any;
        const organisationClaims = this.getOrganisationClaims(claims);
        const firstOrg = organisationClaims[0];

        let storedOrgId: string | null = null;
        try {
            storedOrgId = localStorage.getItem('selectedOrgId');
        } catch {}

        const organisation = (storedOrgId ? organisationClaims.find((o) => o.id === storedOrgId) : null) ?? firstOrg;

        const user: User = {
            homeAccountId: account.homeAccountId,
            name: account.name || '',
            username: account.username,
            givenName: claims?.given_name || '',
            familyName: claims?.family_name || '',
            fullName: `${claims?.given_name || ''} ${claims?.family_name || ''}`.trim(),
            email: claims?.email,
            role: claims?.userRole || Role.User,
            organisation,
            accessToAllStores: this.getAccessToAllStores(claims),
            shopIds: this.getShopIds(claims),
            organisationClaims
        };

        if (this.userCallback) {
            this.userCallback(user);
        }

        if (user.organisation) {
            apiClient.setOrganisationId(user.organisation.id);
        }
    }

    getOrganisationClaims(claims: any): OrganisationClaim[] {
        if (!claims?.organisationClaims) return [];

        try {
            const raw =
                typeof claims.organisationClaims === 'string'
                    ? JSON.parse(claims.organisationClaims)
                    : claims.organisationClaims;
            if (!Array.isArray(raw)) return [];
            return raw
                .map((item: any) => ({
                    id: item.Id ?? '',
                    name: item.Name ?? '',
                    activeLicenses: Array.isArray(item.ActiveLicenses)
                        ? (item.ActiveLicenses.filter((l: string) =>
                              Object.values(ActiveLicenses).includes(l as ActiveLicenses)
                          ) as ActiveLicenses[])
                        : []
                }))
                .sort((a, b) => a.name.localeCompare(b.name));
        } catch {
            console.warn('[AuthService] Failed to parse organisationClaims');
            return [];
        }
    }

    getShopIds(claims: any): string[] {
        if (!claims?.shopIds) return [];

        const shopIds = Array.isArray(claims.shopIds) ? claims.shopIds : [claims.shopIds];

        return shopIds.filter((id: any) => typeof id === 'string');
    }

    getAccessToAllStores(claims: any): boolean {
        if (!claims || !claims.accessToAllStores || typeof claims.accessToAllStores !== 'string') return false;

        return String(claims.accessToAllStores).toLowerCase() === 'true';
    }

    async refreshUserClaims(): Promise<void> {
        const account = this.getCurrentAccount();
        if (!account) return;
        try {
            const res = await msalInstance.acquireTokenSilent({
                ...silentRequest,
                account,
                forceRefresh: true
            });
            await this.setUserFromAccount(res.account ?? account);
        } catch {
            await this.loginRedirect();
        }
    }
}

export const authService = new AuthService();
